/**
 * Publication multi-réseaux — Lot 2 (2026-09-03). Hooks react-query sur
 * /publishing/* et /media. Le flag serveur `publishing_enabled` gouverne
 * l'affichage ; ces hooks ne sont montés que derrière lui.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api';

export type Platform = 'instagram' | 'facebook' | 'linkedin' | 'tiktok';
export const PLATFORMS: Platform[] = ['instagram', 'facebook', 'linkedin', 'tiktok'];

export interface PublishingAccount {
  id: string;
  platform: Platform;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
  followers_count: number | null;
  status: 'active' | 'needs_reconnect' | 'disconnected';
  connected_at: string;
}

export interface PublishingLimits {
  accountsPerMember: number;
  publicationsPerDay: number;
  mediaPerDay: number;
}

export interface StoredMedia { key: string; url: string; width: number; height: number; bytes: number }

export interface TikTokInput {
  privacy_level: 'PUBLIC_TO_EVERYONE' | 'MUTUAL_FOLLOW_FRIENDS' | 'FOLLOWER_OF_CREATOR' | 'SELF_ONLY';
  allow_comment: boolean;
  allow_duet: boolean;
  allow_stitch: boolean;
  draft?: boolean;
}

export interface PublishBody {
  caption: string;
  media_key: string;
  target_account_ids: string[];
  scheduled_for?: string;
  ai_generated: boolean;
  origin: 'idea' | 'visual' | 'library' | 'free';
  origin_ref?: string;
  tiktok?: TikTokInput;
}

export interface CalendarTarget {
  account_id: string;
  platform: Platform;
  status: 'pending' | 'scheduled' | 'published' | 'failed' | 'deleted';
  published_url: string | null;
  error: string | null;
  username: string | null;
}

export interface CalendarItem {
  id: string;
  caption: string;
  media_key: string | null;
  ai_generated: boolean;
  origin: string;
  scheduled_for: string | null;
  status: 'pending' | 'scheduled' | 'published' | 'partial' | 'failed' | 'cancelled';
  created_at: string;
  targets: CalendarTarget[];
}

const ACCOUNTS_KEY = ['publishing', 'accounts'] as const;

export function useAccounts(enabled = true) {
  return useQuery({
    queryKey: ACCOUNTS_KEY,
    queryFn: () => apiRequest<{ accounts: PublishingAccount[]; limits: PublishingLimits }>('/publishing/accounts'),
    enabled,
    staleTime: 30_000,
  });
}

export function useConnect() {
  return useMutation({
    mutationFn: (platform: Platform) =>
      apiRequest<{ url: string }>('/publishing/connect', { method: 'POST', body: { platform } }),
  });
}

export function useDisconnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (accountId: string) => apiRequest<void>(`/publishing/accounts/${accountId}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export function useTiktokOptions(accountId: string | null) {
  return useQuery({
    queryKey: ['publishing', 'tiktok-options', accountId],
    queryFn: () => apiRequest<{ privacyLevels: string[]; canPost: boolean }>(`/publishing/accounts/${accountId}/tiktok-options`),
    enabled: !!accountId,
    staleTime: 5 * 60_000,
  });
}

export function useUploadMedia() {
  return useMutation({
    mutationFn: (input: { data_url: string; source: 'upload' | 'visual' }) =>
      apiRequest<StoredMedia>('/media', { method: 'POST', body: input }),
  });
}

export function usePublish() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PublishBody) => apiRequest<{ id: string; status: string }>('/publishing/posts', { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['publishing', 'calendar'] }),
  });
}

export function useCalendar(fromIso: string, toIso: string, enabled = true) {
  return useQuery({
    queryKey: ['publishing', 'calendar', fromIso, toIso],
    queryFn: () =>
      apiRequest<{ items: CalendarItem[] }>(`/publishing/posts?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`),
    enabled,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

export function useCancelPublication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest<void>(`/publishing/posts/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['publishing', 'calendar'] }),
  });
}

/** Libellé i18n d'un réseau. */
export const PLATFORM_LABEL_KEY: Record<Platform, string> = {
  instagram: 'accounts.platformInstagram',
  facebook: 'accounts.platformFacebook',
  linkedin: 'accounts.platformLinkedin',
  tiktok: 'accounts.platformTiktok',
};

export const PLATFORM_TINT: Record<Platform, string> = {
  instagram: '#ec4899',
  facebook: '#3b82f6',
  linkedin: '#0a66c2',
  tiktok: '#111827',
};
